import { noChange } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import { PartType, type ElementPart, type PartInfo } from 'lit/directive.js'
import { type Subscription, asapScheduler, filter } from 'rxjs'
import { reducedMotion$ } from './reduced-motion'

export interface StatusOptions {
	label?: string
	state: 'working' | 'done'
}

/** Paints progress or completion inside an existing card wrapper without changing its layout. */
class StatusDirective extends AsyncDirective {
	private element?: HTMLElement
	private caption?: HTMLSpanElement
	private seam?: HTMLSpanElement
	private corner?: HTMLSpanElement
	private seen = false
	private animation?: Animation
	private motion?: Subscription
	private originalPosition?: string
	private state?: 'working' | 'done'
	private paintedState?: 'working' | 'done'
	private mounting?: Subscription
	private label = 'Done'

	constructor(info: PartInfo) {
		super(info)
		if (info.type !== PartType.ELEMENT) throw new Error('status() belongs on a card wrapper element')
	}

	render(_options: StatusOptions | false) {
		return noChange
	}

	override update(part: ElementPart, [options]: [options: StatusOptions | false]) {
		const animate = this.seen && this.state !== 'done' && options !== false && options.state === 'done'
		this.seen = true
		this.state = options === false ? undefined : options.state
		this.label = options === false ? '' : (options.label ?? (options.state === 'done' ? 'Done' : 'Working'))
		if (!(part.element instanceof HTMLElement)) return noChange
		this.element = part.element
		this.mounting?.unsubscribe()
		if (!this.state || !this.isConnected) {
			this.cleanup()
			return noChange
		}
		if (!this.element.isConnected) {
			// Lit commits element directives before inserting their fragment into the document.
			this.mounting = asapScheduler.schedule(() => {
				if (this.isConnected && this.element?.isConnected && this.state) this.paint(false)
			})
		} else this.paint(animate)
		return noChange
	}

	private paint(animate: boolean) {
		if (!this.element) return
		if (!this.caption) {
			if (getComputedStyle(this.element).position === 'static') {
				this.originalPosition = this.element.style.position
				this.element.style.position = 'relative'
			}
			this.seam = document.createElement('span')
			this.seam.dataset.schmancyStatusSeam = ''
			this.seam.setAttribute('aria-hidden', 'true')
			Object.assign(this.seam.style, {
				position: 'absolute',
				inset: '0',
				boxShadow: 'inset 0 0 0 1px color-mix(in srgb, currentColor 28%, transparent)',
				overflow: 'hidden',
				borderRadius: 'inherit',
				pointerEvents: 'none',
			})
			this.corner = document.createElement('span')
			this.corner.dataset.schmancyStatusCorner = ''
			this.corner.setAttribute('aria-hidden', 'true')
			Object.assign(this.corner.style, {
				position: 'absolute',
				insetBlockStart: '0',
				insetInlineStart: '0',
				width: 'min(24px, 100%)',
				height: 'min(24px, 100%)',
				borderBlockStart: '1px solid currentColor',
				borderInlineStart: '1px solid currentColor',
				borderStartStartRadius: 'inherit',
				opacity: '0.55',
				pointerEvents: 'none',
			})
			this.caption = document.createElement('span')
			this.caption.dataset.schmancyStatusCaption = ''
			Object.assign(this.caption.style, {
				position: 'absolute',
				width: '1px',
				height: '1px',
				padding: '0',
				margin: '0',
				overflow: 'hidden',
				clipPath: 'inset(50%)',
				whiteSpace: 'nowrap',
			})
			this.seam.append(this.corner)
			this.element.append(this.seam, this.caption)
			this.motion = reducedMotion$.pipe(filter(Boolean)).subscribe(() => this.animation?.cancel())
		}
		this.caption.textContent = this.label
		if (this.seam)
			this.seam.style.boxShadow = `inset 0 0 0 1px color-mix(in srgb, currentColor ${this.state === 'working' ? 32 : 28}%, transparent)`
		for (const edge of [this.seam, this.corner])
			if (edge) edge.style.color = `var(--schmancy-sys-color-${this.state === 'done' ? 'success' : 'primary'}-default)`
		if (this.paintedState !== this.state) {
			this.animation?.cancel()
			if (this.corner) {
				Object.assign(this.corner.style, {
					width: this.state === 'working' ? '1px' : 'min(24px, 100%)',
					background: this.state === 'working' ? 'currentColor' : '',
					borderBlockStart: this.state === 'working' ? '0' : '1px solid currentColor',
					borderInlineStart: this.state === 'working' ? '0' : '1px solid currentColor',
				})
				if (this.state === 'working' && !reducedMotion$.value) {
					this.animation = this.corner.animate(
						[
							{ transform: 'translateY(0)', opacity: 0.32 },
							{
								transform: `translateY(${Math.max(0, this.element.getBoundingClientRect().height - 24)}px)`,
								opacity: 0.55,
							},
						],
						{ duration: 2400, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out' },
					)
				}
			}
			this.paintedState = this.state
		}
		if (animate && !reducedMotion$.value && this.seam) {
			this.animation?.cancel()
			this.animation = this.seam.animate([{ filter: 'brightness(1.6)' }, { filter: 'brightness(1)' }], {
				duration: 600,
				easing: 'ease-out',
				iterations: 1,
			})
		}
	}

	private cleanup() {
		this.mounting?.unsubscribe()
		this.mounting = undefined
		this.animation?.cancel()
		this.animation = undefined
		this.motion?.unsubscribe()
		this.motion = undefined
		this.caption?.remove()
		this.seam?.remove()
		this.corner?.remove()
		this.caption = undefined
		this.seam = undefined
		this.corner = undefined
		this.paintedState = undefined
		if (this.element && this.originalPosition !== undefined && this.element.style.position === 'relative') {
			this.element.style.position = this.originalPosition
		}
		this.originalPosition = undefined
	}

	override disconnected() {
		this.cleanup()
	}

	override reconnected() {
		if (this.state) this.paint(false)
	}
}

export const status = directive(StatusDirective)
